import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { NdaAgreement } from "@/components/NdaAgreement";
import { TermsConsent } from "@/components/TermsConsent";
import orbitIcon from "@/assets/orbit-icon.png";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { onAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => { setInvite(data); setName(data.name || ""); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Invite not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ndaAccepted) {
      setErr("Please read and agree to the Beta Participant NDA to continue.");
      return;
    }
    if (!termsAccepted) {
      setErr("Please agree to the Terms and Conditions and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name, password, nda_accepted: true, nda_signature: name, terms_accepted: true });
      onAuth(data);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not accept invite");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "radial-gradient(120% 90% at 50% 0%, #0d2350 0%, #071232 45%, #050b20 100%)" }}>
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl" data-testid="accept-invite-card">
        <div className="flex items-center gap-2 mb-6">
          <img src={orbitIcon} alt="Orbit" className="h-9 w-auto" />
          <span className="font-display text-xl font-extrabold tracking-tight">Project Management <span className="text-primary">Orchestrator</span></span>
        </div>
        {err && !invite ? (
          <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="invite-error">{err}</p>
        ) : !invite ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <form onSubmit={submit} data-testid="accept-invite-form">
            <p className="overline text-muted-foreground mb-1">You've been invited</p>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-1">Join the Project Management Orchestrator beta</h1>
            <p className="text-sm text-muted-foreground mb-6">Set a password for <b>{invite.email}</b> to get started.</p>
            {err && <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="invite-submit-error">{err}</div>}
            <div className="space-y-4">
              <div>
                <Label htmlFor="ai-name">Your name</Label>
                <Input id="ai-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="invite-name-input" />
              </div>
              <div>
                <Label htmlFor="ai-pw">Choose a password</Label>
                <Input id="ai-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" data-testid="invite-password-input" />
              </div>
              <NdaAgreement accepted={ndaAccepted} onAcceptedChange={setNdaAccepted} />
              <TermsConsent accepted={termsAccepted} onAcceptedChange={setTermsAccepted} />
            </div>
            <Button type="submit" disabled={loading || !ndaAccepted || !termsAccepted} className="w-full mt-6 h-11 gap-2" data-testid="invite-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Activate my account</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
